import React, { KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

/**
 * `common/DataTable` (M-UX-2.3, architecture-ux-restructure.md §4.3 + §13.2).
 *
 * Client-side sort (no fetch), empty state, and roving-tabindex keyboard
 * navigation (↑/↓ move the active row, Enter opens it via `onRowClick`).
 * Keyboard nav is part of the DataTable API itself — §13.2 is explicit that
 * this is "kein Advisor-Fork": every consumer gets it, Advisor-Mode does not
 * branch the component.
 *
 * Density (§13.2 "DATEV-2030"): row height reads `--table-row-height`
 * (`styles/tokens.css`, M-UX-2.2) via inline style, so it automatically
 * compacts once a layout root sets `data-mode="advisor"` (wired in W7) —
 * no JS branching needed here. Numeric columns opt into `tabular-nums` via
 * `Column.numeric`.
 */
export interface Column<T> {
  /** Stable column id — doubles as the sort key. */
  id: string;
  header: ReactNode;
  /** Renders a cell's content for a row. */
  accessor: (row: T) => ReactNode;
  /** Value used for client-side sorting. Omit to make the column unsortable. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'center' | 'right';
  /** Applies `tabular-nums` — use for monetary/aligned figures. */
  numeric?: boolean;
  /**
   * Responsively drop the column below a breakpoint: renders th+td with
   * `hidden {bp}:table-cell`, so on narrow viewports the table sheds
   * lower-priority columns instead of overflowing into an inner scrollbox.
   */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Clamp the cell content to a single line: wraps the accessor output in a
   * `truncate` box of the given max-width class (e.g. `'max-w-[14rem]'`) and,
   * when the accessor yields plain text, mirrors that text into the cell's
   * `title` for full-value hover/A11y. Non-string accessors are still clamped
   * but carry no title.
   */
  truncate?: string;
  /**
   * Pin the column to the right edge (`position: sticky; right: 0`) so the
   * action column is never the one that scrolls off. The primitive supplies
   * an opaque token background + left divider so scrolling content never
   * bleeds through the pinned cell.
   */
  sticky?: 'right';
  className?: string;
}

export interface SortState {
  id: string;
  dir: 'asc' | 'desc';
}

export type DataTableDensity = 'comfortable' | 'compact';

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  empty: ReactNode;
  onRowClick?: (row: T) => void;
  initialSort?: SortState;
  /**
   * NEU (M-A, architecture-ux-density-v2.md §5.5): Zeilendichte. Default
   * `'comfortable'` = Bestandsverhalten (0 Diff für Bestands-Consumer).
   * `'compact'` setzt die Root-Klasse `fb-datatable--compact`; die zugehörige
   * Höhe/Padding-Kaskade lebt in `styles/tokens.css` (kein JS-Branch, kein
   * Advisor-Fork — der `[data-mode="advisor"]`-Block gewinnt weiterhin).
   */
  density?: DataTableDensity;
  /**
   * NEU (M-A, §5.5): optionale Detail-Zeile UNTER einer Datenzeile
   * (z. B. Mahnhistorie, E-Mail-Versand-Formular). Rückgabe `null`/`undefined`
   * ⇒ keine Detail-Zeile. Gerendert als eigenes `<tr>` mit einer Zelle
   * `colSpan={columns.length}`; NICHT Teil der roving-tabindex-Navigation
   * (↑/↓ überspringt sie, sie ist nicht fokussierbar); Klicks in der
   * Detail-Zeile lösen KEIN `onRowClick` aus.
   */
  renderRowDetail?: (row: T) => ReactNode;
  /**
   * NEU (M-D): optionale, zeilen-abhängige Zusatz-CSS-Klassen für die Daten-`<tr>`
   * (z. B. URL-Highlight der via `?id=` angesteuerten Zeile in den Archiven).
   * Rein additiv — wird an die bestehende Klassen-Kaskade angehängt und ändert
   * weder Fokus-/Roving-Tabindex-Verhalten noch die Detail-Zeile. `undefined`/
   * leerer String ⇒ kein Zusatz (0 Diff für Bestands-Consumer).
   */
  rowClassName?: (row: T) => string | undefined;
}

const ALIGN_CLASSES: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const HIDE_BELOW_CLASSES: Record<NonNullable<Column<unknown>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

/**
 * Pinned-column classes. The cell needs an OPAQUE background (a semi-transparent
 * one would let the horizontally scrolling content show through), a hover shade
 * that tracks the row (`group-hover`, so the `tr` must carry `group`), and a
 * left divider so the pin reads as intentional. Header vs. body differ only in
 * their resting shade (header sits on `bg-surface-inset`).
 */
const STICKY_TD_CLASSES =
  'sticky right-0 z-10 bg-surface-raised group-hover:bg-surface-inset border-l border-default';
const STICKY_TH_CLASSES = 'sticky right-0 z-10 bg-surface-inset border-l border-default';

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
  onRowClick,
  initialSort,
  density = 'comfortable',
  renderRowDetail,
  rowClassName,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState | undefined>(initialSort);
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.id === sort.id);
    if (!column?.sortValue) return rows;
    const sortValue = column.sortValue;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortValue(a);
      const vb = sortValue(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  }, [rows, sort, columns]);

  // Keep the active row in range when the row set shrinks (e.g. after a filter).
  // Deliberately does NOT call `.focus()` — only clamps the roving-tabindex
  // pointer. Moving actual DOM focus must stay tied to a real user action
  // (keyboard nav below / native focus events), never to a render effect —
  // otherwise mounting (or re-sorting/re-filtering) the table would steal
  // focus away from whatever the user was doing (🔴 bug, test-report).
  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(sortedRows.length - 1, 0)));
  }, [sortedRows.length]);

  const toggleSort = (column: Column<T>) => {
    if (!column.sortValue) return;
    setSort((current) => {
      if (current?.id !== column.id) return { id: column.id, dir: 'asc' };
      return { id: column.id, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  /**
   * Moves the roving-tabindex pointer AND the actual DOM focus in direct
   * response to the keyboard event that caused it. This intentionally does
   * NOT go through a `useEffect` on `activeIndex` — an effect would also
   * fire on the initial mount (`activeIndex` starts at 0) and steal focus
   * from the rest of the page with zero user interaction.
   */
  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, index: number, row: T) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = Math.min(index + 1, sortedRows.length - 1);
      setActiveIndex(next);
      rowRefs.current[next]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = Math.max(index - 1, 0);
      setActiveIndex(prev);
      rowRefs.current[prev]?.focus();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  // `fb-datatable--compact` scopes the compact row-height/padding cascade in
  // tokens.css; comfortable adds nothing (0 diff for existing consumers).
  const rootClass = density === 'compact' ? 'fb-datatable fb-datatable--compact' : 'fb-datatable';

  if (sortedRows.length === 0) {
    return (
      <div className={`${rootClass} rounded-card border border-default bg-surface-raised p-8 text-center text-secondary`}>
        {empty}
      </div>
    );
  }

  /**
   * Zählt, wie oft ein `rowKey`-Wert in DIESEM Render schon vergeben wurde, und
   * hängt bei Wiederholungen ein Suffix an.
   *
   * WARUM: `rowKey` ist ein Vertrag ("eindeutig pro Zeile"), den der Consumer
   * verletzen kann — und beim Kunden-Löschen im Nightly genau das tat: eine
   * Wettlaufsituation legte zweimal denselben Kunden in die Liste, also zweimal
   * denselben React-Key. Zwei Kinder mit identischem Key sind für React ein
   * undefinierter Reconciliation-Zustand; beim Entfernen blieb eines der `<tr>`
   * als verwaistes DOM-Element stehen, DAUERHAFT und ohne jede Chance, es je
   * wieder loszuwerden — der State war korrekt (Zeile weg), das DOM nicht.
   * Ein solcher Zustand ist von außen nicht mehr reparierbar und war
   * entsprechend schwer zu deuten.
   *
   * Die Ursache gehört zum Consumer (dort behoben), aber die Verstärkung
   * gehört hierher: ein doppelter Key darf höchstens eine doppelte Zeile
   * ergeben, niemals eine unsterbliche. Bei eindeutigen Keys — dem Normalfall
   * aller Consumer — ist diese Zählung ein exaktes No-op.
   */
  const rowKeyUses = new Map<string, number>();
  const uniqueRowKey = (row: T): string => {
    const base = rowKey(row);
    const seen = (rowKeyUses.get(base) ?? 0) + 1;
    rowKeyUses.set(base, seen);
    return seen === 1 ? base : `${base}#dup${seen}`;
  };

  return (
    <div className={`${rootClass} overflow-x-auto rounded-card border border-default`}>
      <table className="min-w-full divide-y divide-default">
        <thead className="bg-surface-inset">
          <tr>
            {columns.map((column) => {
              const sortable = Boolean(column.sortValue);
              const isSorted = sort?.id === column.id;
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={isSorted ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={[
                    'px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted',
                    ALIGN_CLASSES[column.align ?? 'left'],
                    column.hideBelow ? HIDE_BELOW_CLASSES[column.hideBelow] : '',
                    column.sticky === 'right' ? STICKY_TH_CLASSES : '',
                    column.className ?? '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column)}
                      className="inline-flex items-center gap-1 hover:text-primary"
                    >
                      {column.header}
                      <span aria-hidden="true" className="text-[0.65rem]">
                        {isSorted ? (sort!.dir === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-default">
          {sortedRows.map((row, index) => {
            // Detail row (renderRowDetail) sits UNDER its data row, spans all
            // columns, is NOT focusable/roving-tabindex-navigable, and swallows
            // its own clicks so it never triggers the parent onRowClick.
            const detail = renderRowDetail ? renderRowDetail(row) : null;
            return (
              <React.Fragment key={uniqueRowKey(row)}>
                <tr
                  ref={(el) => {
                    rowRefs.current[index] = el;
                  }}
                  tabIndex={index === activeIndex ? 0 : -1}
                  onFocus={() => setActiveIndex(index)}
                  onKeyDown={(event) => handleRowKeyDown(event, index, row)}
                  onClick={() => onRowClick?.(row)}
                  style={{ height: 'var(--table-row-height)' }}
                  className={[
                    'group',
                    onRowClick ? 'cursor-pointer hover:bg-surface-inset' : '',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset',
                    rowClassName?.(row) ?? '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {columns.map((column) => {
                    const cell = column.accessor(row);
                    return (
                      <td
                        key={column.id}
                        className={[
                          'px-4 py-2 text-sm text-primary',
                          ALIGN_CLASSES[column.align ?? 'left'],
                          column.numeric ? 'tabular-nums' : '',
                          column.hideBelow ? HIDE_BELOW_CLASSES[column.hideBelow] : '',
                          column.sticky === 'right' ? STICKY_TD_CLASSES : '',
                          column.className ?? '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {column.truncate ? (
                          <div
                            className={['truncate', column.truncate].join(' ')}
                            title={typeof cell === 'string' ? cell : undefined}
                          >
                            {cell}
                          </div>
                        ) : (
                          cell
                        )}
                      </td>
                    );
                  })}
                </tr>
                {detail ? (
                  <tr className="fb-datatable__detail-row bg-surface-inset/40">
                    <td
                      colSpan={columns.length}
                      className="px-4 py-2 text-sm text-primary"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {detail}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
